import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import type { PostgresIntegration } from "@p0security/shared/integrations/resources/postgres/components";
import type { RecursiveReadonly } from "@p0security/shared/types/util";
import { Button, Divider, Flex, Form, Input, Space, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { isEqual, noop } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

type RowSecurityPolicyProps = {
  config: PostgresIntegration["row-security-policy"][string];
};

type RowSecurityPolicyEntry = {
  /** TABLE that this policy affects */
  table: string;
  /** SELECT constraint */
  using: string;
  /** UPDATE constraint */
  check?: string;
};

type RowSecurityPolicy = { entries: RowSecurityPolicyEntry[] };

type PolicyEntryProps = {
  name: number;
  remove: (index: number[] | number) => void;
};

const defaultRowSecurityPolicy: RecursiveReadonly<RowSecurityPolicy> = {
  entries: [],
} as const;

const PreparePlaceholder: React.FC = () => (
  <>
    Use a <Typography.Text code>?</Typography.Text> placeholder to bind the
    request constraint
  </>
);

const PolicyEntry: React.FC<PolicyEntryProps> = ({ name, remove }) => {
  const handleRemove = useCallback(() => remove(name), [name, remove]);

  return (
    <Flex vertical gap={0} style={{ width: "600px" }}>
      <Form.Item
        name={[name, "table"]}
        label="TABLE"
        required
        layout="horizontal"
      >
        <Input />
      </Form.Item>
      <Form.Item
        name={[name, "using"]}
        label="USING"
        required
        layout="horizontal"
        extra={<PreparePlaceholder />}
      >
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        name={[name, "check"]}
        label="CHECK"
        layout="horizontal"
        extra={<PreparePlaceholder />}
      >
        <Input.TextArea />
      </Form.Item>
      <Button
        onClick={handleRemove}
        icon={<MinusCircleOutlined />}
        danger
        style={{ alignSelf: "flex-start" }}
      >
        Remove
      </Button>
    </Flex>
  );
};

export const RowSecurityPolicyFooter: React.FC<RowSecurityPolicyProps> = ({
  config,
}) => {
  const [form] = useForm<RowSecurityPolicy>();

  const savedPolicy = useMemo(
    () =>
      config?.policy
        ? (JSON.parse(config.policy) as RowSecurityPolicy)
        : defaultRowSecurityPolicy,
    [config?.policy]
  );

  useEffect(() => {
    form.setFieldsValue(savedPolicy as RowSecurityPolicy);
  }, [savedPolicy, form]);

  const values = Form.useWatch([], form);
  const isClean = useMemo(
    () => !!values && isEqual(savedPolicy, values),
    [savedPolicy, values]
  );

  return (
    <Form form={form} onFinish={noop}>
      <Form.List name="entries">
        {(entries, { add, remove }) => (
          <>
            <Space direction="vertical" split={<Divider />}>
              <Flex gap={16} justify="space-between">
                <Button onClick={add} icon={<PlusCircleOutlined />}>
                  Add entry
                </Button>
                <Button
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  disabled={isClean}
                  type="primary"
                >
                  Submit
                </Button>
              </Flex>
              {!entries.length && (
                <Typography.Text type="secondary">
                  Select &quot;
                  <PlusCircleOutlined /> Add entry&quot; to add a policy entry
                </Typography.Text>
              )}
              {entries.map(({ key, name }) => (
                <PolicyEntry key={key} name={name} remove={remove} />
              ))}
            </Space>
          </>
        )}
      </Form.List>
    </Form>
  );
};
